import { useState, useEffect } from "react";
import { useQuery } from "@apollo/client";
import usePagination from "@/hooks/usePagination";
import { Controller } from "react-hook-form";
import SearchInput from "@/components/SearchInput";
import { upperCase, reject, isEmpty } from "lodash";
import { useSearch } from "@/hooks/useSearch";
import { FormProvider, useForm } from "react-hook-form";
import DateSelect from "@/components/DateSelect";
import MultiSelect from "@/components/MultiSelect";
import SegmentedButton from "@/components/SegmentedButton";
import { DateValueType } from "react-tailwindcss-datepicker";
import FacilityAutocomplete from "../../../_components/FacilityAutocomplete";
import SectionAutocomplete from "../../../_components/SectionAutocomplete";
import { ExpandedFiltersWrapper } from "@/components/ExpandedFilters";
import { get } from "lodash";
import { GET_CLASSIFICATIONS } from "../../../queries";
import { ReportType, Variables } from "./PeopleTable";
import { PeopleRow } from "./PeopleTable";
import { useParams } from "@/hooks/useSearchParams";

export type FormValues = {
  memberClassifications: string[];
  program: string[];
  sectionIds: string[];
  facilityIds: string[];
  suspensionDateRange: DateValueType | null;
  terminationDateRange: DateValueType | null;
};

const REESTABLISHED_OPTIONS = [
  { value: "current", label: "Currently Terminated" },
  { value: "reestablished", label: "Re-established" },
  { value: "all", label: "All" }
];

const reestablishedToSegment = (value: boolean | null | undefined) => {
  if (value === true) return "reestablished";
  if (value === false) return "current";
  return "all";
};

const segmentToReestablished = (value: string | null): boolean | null => {
  if (value === "reestablished") return true;
  if (value === "current") return false;
  return null;
};

export const PeopleTableFilters = ({
  reportType,
  defaultVariables,
  peopleData,
  setVariables,
  variables,
  data
}: {
  reportType: ReportType;
  defaultVariables: Variables;
  peopleData: PeopleRow[];
  setVariables: React.Dispatch<React.SetStateAction<Variables>>;
  variables: Variables;
  data: any;
}) => {
  const isTerminations = reportType === "terminations";
  let { params, addSearchParam, deleteSearchParams } = useParams();
  const { renderFooter, resetPagination } = usePagination();
  const { searchValue, control: controlSearch, setValue: setSearchValue } = useSearch("name", params.name || "");

  const [selectedValues, setSelectedValues] = useState<string[]>(params.programTypes || []);
  const [selectedClassificationValues, setSelectedClassificationValues] = useState<string[]>(
    params.memberClassifications || []
  );

  const { data: classificationData } = useQuery(GET_CLASSIFICATIONS);

  const form = useForm<FormValues>();
  const { control, reset, watch } = form;

  const sectionIds = watch("sectionIds");
  const facilityIds = watch("facilityIds");

  useEffect(() => {
    setVariables((prevValue) => ({
      ...prevValue,
      sectionIds: sectionIds,
      facilityIds: facilityIds
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sectionIds, facilityIds]);

  useEffect(() => {
    setVariables((prevValue) => {
      const { search: _search, ...restVariables } = prevValue;
      return {
        ...restVariables,
        ...(searchValue && { search: searchValue })
      };
    });
  }, [searchValue, setVariables]);

  const filters = {
    suspendedDateRange: !!variables?.suspendedDateRange?.from && !!variables?.suspendedDateRange?.to,
    terminatedDateRange: !!variables?.terminatedDateRange?.from || !!variables?.terminatedDateRange?.to,
    reestablished: isTerminations && variables.reestablished !== false,
    section: params.sectionIds?.length > 0,
    facility: params.facilityIds?.length > 0,
    clasifications: selectedClassificationValues.length > 0,
    program: selectedValues.length > 0
  };

  const classificationsNodes = get(classificationData, "classifications.nodes");
  const classificationDataNodes =
    classificationsNodes?.map((node: any) => ({
      value: node.name,
      label: node.name
    })) || [];

  const onClearAll = () => {
    setVariables({ ...defaultVariables });
    setSelectedValues([]);
    setSelectedClassificationValues([]);
    reset({
      suspensionDateRange: { startDate: null, endDate: null },
      terminationDateRange: { startDate: null, endDate: null }
    });
    deleteSearchParams([
      "memberClassifications[]",
      "programTypes[]",
      "facilityIds[]",
      "sectionIds[]",
      "facility",
      "section",
      "reestablished",
      "name"
    ]);
    setSearchValue("name", "");
  };

  return (
    <div className="flex flex-col sm:flex-row md:items-center justify-between">
      <div className={`flex justify-between md:justify-start gap-3 md:flex-row w-full sm:pr-4`}>
        <FormProvider {...form}>
          <div className="w-full sm:max-w-[320px] flex-shrink-1 md:flex-shrink-0 focus:outline-none">
            <Controller
              name="name"
              control={controlSearch}
              render={({ field }) => (
                <SearchInput
                  placeholder="Search People"
                  query={field.value}
                  onChange={(e) => {
                    field.onChange(e);
                    resetPagination();
                    addSearchParam("name", e);
                  }}
                  onClear={() => {
                    deleteSearchParams("name");
                    setSearchValue("name", "");
                  }}
                />
              )}
            />
          </div>
          <ExpandedFiltersWrapper
            filters={filters}
            items={[
              {
                component: (
                  <Controller
                    name="memberClassifications"
                    control={control}
                    render={() => (
                      <MultiSelect
                        label="Classification"
                        selectedValues={selectedClassificationValues}
                        options={classificationDataNodes}
                        onSelect={(value, checked) => {
                          const status = checked
                            ? [...selectedClassificationValues, value]
                            : selectedClassificationValues.filter((v) => v !== value);
                          setSelectedClassificationValues(reject(status, isEmpty));
                          setVariables((prevValue) => ({
                            ...prevValue,
                            memberClassifications: reject(status, isEmpty)
                          }));
                          addSearchParam("memberClassifications[]", reject(status, isEmpty).join(","));
                        }}
                        onClear={() => {
                          setSelectedClassificationValues([]);
                          setVariables((prevValue) => ({
                            ...prevValue,
                            memberClassifications: []
                          }));
                          deleteSearchParams("memberClassifications[]");
                        }}
                        className="w-full"
                      />
                    )}
                  />
                ),
                onlyMobile: true
              },
              {
                component: (
                  <FacilityAutocomplete
                    sectionIds={variables?.sectionIds}
                    addSearchParam={addSearchParam}
                    deleteSearchParams={deleteSearchParams}
                    params={params}
                  />
                ),
                onlyMobile: true
              },
              {
                component: (
                  <Controller
                    name="program"
                    control={control}
                    render={() => (
                      <MultiSelect
                        label="Program"
                        selectedValues={selectedValues}
                        options={[
                          { value: "MEMBER", label: "Member (MB)" },
                          { value: "ASSOCIATE", label: "Associate (AP)" },
                          { value: "AFFILIATE", label: "Affiliate (AF)" }
                        ]}
                        onSelect={(value, checked) => {
                          const status = checked
                            ? [...selectedValues, value]
                            : selectedValues.filter((v) => v !== value);
                          const statusValues = status.map((status) => upperCase(status));
                          setSelectedValues(reject(status, isEmpty));
                          setVariables((prevValue) => ({
                            ...prevValue,
                            programTypes: reject(statusValues, isEmpty)
                          }));
                          addSearchParam("programTypes[]", reject(status, isEmpty).join(","));
                        }}
                        onClear={() => {
                          setSelectedValues([]);
                          setVariables((prevValue) => ({
                            ...prevValue,
                            programTypes: []
                          }));
                          deleteSearchParams("programTypes[]");
                        }}
                        className="w-full"
                      />
                    )}
                  />
                )
              },
              ...(isTerminations
                ? [
                    {
                      component: (
                        <div className="flex flex-col gap-1 min-w-[280px]">
                          <span className="text-xs text-sys-brand-on-surface-variant px-1">Re-establishment</span>
                          <SegmentedButton
                            options={REESTABLISHED_OPTIONS}
                            value={reestablishedToSegment(variables.reestablished)}
                            onChange={(value) => {
                              const next = segmentToReestablished(value);
                              setVariables((prevValue) => ({
                                ...prevValue,
                                reestablished: next
                              }));
                              if (next === null) {
                                deleteSearchParams("reestablished");
                              } else {
                                addSearchParam("reestablished", String(next));
                              }
                              resetPagination();
                            }}
                            fullWidth
                          />
                        </div>
                      )
                    }
                  ]
                : []),
              // Suspension date range stays hidden (previously broken / out of scope).
              // Only enable Termination Date for the NPDU terminations report.
              ...(isTerminations
                ? [
                    {
                      component: (
                        <Controller
                          name="terminationDateRange"
                          control={control}
                          render={({ field }) => {
                            return (
                              <DateSelect
                                placeholder="Select Dates"
                                label="Termination Date"
                                useModal={true}
                                value={field.value}
                                onChange={(e) => {
                                  field.onChange(e);
                                  if (typeof e === "object" && e !== null) {
                                    setVariables((prevValue) => ({
                                      ...prevValue,
                                      terminatedDateRange: {
                                        from: e.startDate,
                                        to: e.endDate
                                      }
                                    }));
                                    resetPagination();
                                  }
                                }}
                              />
                            );
                          }}
                        />
                      )
                    }
                  ]
                : []),
              {
                component: (
                  <SectionAutocomplete
                    addSearchParam={addSearchParam}
                    deleteSearchParams={deleteSearchParams}
                    params={params}
                    name="section"
                  />
                )
              }
            ]}
            onClearAll={onClearAll}
          />
        </FormProvider>
      </div>
      <div className="mt-3 sm:mt-0">
        {renderFooter(data?.people, {
          totalCount: data?.people?.totalCount || 0,
          pageCount: peopleData.length,
          variant: "compact"
        })()}
      </div>
    </div>
  );
};

export default PeopleTableFilters;
